import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import color from "../../../Constants/colors";
import { VoicesSearchMode } from "../../../Enums/voices";
import {
  VoicesCampaign,
  VoicesCampaignPayload,
} from "../../../Models/voicesCampaigns";
import { useSaveVoicesCampaign } from "../../../Query/useVoicesCampaigns";
import { ComoButton } from "../../Common/ComoButton";
import VoicesField from "../Common/VoicesField";
import VoicesInput from "../Common/VoicesInput";
import VoicesTermsInput from "../Common/VoicesTermsInput";

interface Props {
  open: boolean;
  campaign: VoicesCampaign | null;
  onClose: () => void;
  onSaved?: (campaign: VoicesCampaign) => void;
}

const MAX_TOPIC_FOCUS_CHARS = 1000;

const EMPTY_CAMPAIGN: VoicesCampaignPayload = {
  name: "",
  description: "",
  instructions: "",
  topic_focus: "",
  search_terms: [],
  search_mode: VoicesSearchMode.CLAUDE_NATIVE,
  starts_on: null,
  ends_on: null,
};

const VoicesCampaignDialog: React.FC<Props> = ({
  open,
  campaign,
  onClose,
  onSaved,
}) => {
  const { t } = useTranslation("voicesCampaigns");
  const saveCampaign = useSaveVoicesCampaign();
  const [form, setForm] = useState<VoicesCampaignPayload>(EMPTY_CAMPAIGN);

  useEffect(() => {
    if (!open) return;
    setForm(
      campaign
        ? {
            name: campaign.name,
            description: campaign.description,
            instructions: campaign.instructions,
            topic_focus: campaign.topic_focus,
            search_terms: campaign.search_terms,
            search_mode: campaign.search_mode,
            starts_on: campaign.starts_on,
            ends_on: campaign.ends_on,
          }
        : EMPTY_CAMPAIGN,
    );
  }, [open, campaign]);

  const periodValid =
    !form.starts_on || !form.ends_on || form.ends_on >= form.starts_on;

  const handleSave = () => {
    saveCampaign.mutate(
      { campaignId: campaign ? campaign.id : null, data: form },
      {
        onSuccess: (saved) => {
          onClose();
          onSaved?.(saved as VoicesCampaign);
        },
      },
    );
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ color: color.gray900, fontWeight: 600 }}>
        {campaign ? t("editCampaign") : t("newCampaign")}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <VoicesField label={t("name")}>
            <VoicesInput
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </VoicesField>
          <VoicesField label={t("description")} hint={t("descriptionHint")}>
            <VoicesInput
              multiline
              minRows={2}
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
            />
          </VoicesField>
          <VoicesField label={t("searchTerms")} hint={t("searchTermsHint")}>
            <VoicesTermsInput
              value={form.search_terms}
              onChange={(search_terms) => setForm({ ...form, search_terms })}
              placeholder={t("searchTermsPlaceholder")}
            />
          </VoicesField>
          <VoicesField label={t("topicFocus")} hint={t("topicFocusHint")}>
            <VoicesInput
              multiline
              minRows={2}
              value={form.topic_focus}
              placeholder={t("topicFocusPlaceholder")}
              error={form.topic_focus.length > MAX_TOPIC_FOCUS_CHARS}
              helperText={
                form.topic_focus.length > MAX_TOPIC_FOCUS_CHARS * 0.8
                  ? t("charactersLeft", {
                      count: MAX_TOPIC_FOCUS_CHARS - form.topic_focus.length,
                    })
                  : undefined
              }
              onChange={(e) => setForm({ ...form, topic_focus: e.target.value })}
            />
          </VoicesField>
          <VoicesField label={t("instructions")} hint={t("instructionsHint")}>
            <VoicesInput
              multiline
              minRows={3}
              value={form.instructions}
              onChange={(e) => setForm({ ...form, instructions: e.target.value })}
            />
          </VoicesField>
          <VoicesField label={t("searchMode")} hint={t("searchModeHint")}>
            <VoicesInput
              select
              value={form.search_mode}
              onChange={(e) =>
                setForm({
                  ...form,
                  search_mode: Number(e.target.value) as VoicesSearchMode,
                })
              }
            >
              <MenuItem value={VoicesSearchMode.CLAUDE_NATIVE}>
                {t("searchModeClaude")}
              </MenuItem>
              <MenuItem value={VoicesSearchMode.QMS_AGENT}>
                {t("searchModeAgent")}
              </MenuItem>
            </VoicesInput>
          </VoicesField>
          <Box sx={{ display: "flex", gap: 2 }}>
            <VoicesField label={t("startsOn")}>
              <VoicesInput
                type="date"
                value={form.starts_on ?? ""}
                onChange={(e) =>
                  setForm({ ...form, starts_on: e.target.value || null })
                }
              />
            </VoicesField>
            <VoicesField label={t("endsOn")}>
              <VoicesInput
                type="date"
                value={form.ends_on ?? ""}
                error={!periodValid}
                helperText={periodValid ? undefined : t("periodInvalid")}
                onChange={(e) =>
                  setForm({ ...form, ends_on: e.target.value || null })
                }
              />
            </VoicesField>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={onClose} sx={{ color: color.gray600, textTransform: "none" }}>
          {t("cancel")}
        </Button>
        <ComoButton
          onClick={handleSave}
          disabled={
            !form.name.trim() ||
            !periodValid ||
            form.topic_focus.length > MAX_TOPIC_FOCUS_CHARS ||
            saveCampaign.isPending
          }
        >
          {t("save")}
        </ComoButton>
      </DialogActions>
    </Dialog>
  );
};

export default VoicesCampaignDialog;
